function configuredLimit(value, fallback) {
  const parsed = Number(value ?? fallback)
  if (!Number.isSafeInteger(parsed) || parsed < 1) throw new Error('Limite do simulador inválido na configuração do frontend.')
  return parsed
}

export const MAX_SIMULATION_TEXT_LENGTH = configuredLimit(import.meta.env?.VITE_DEADLINE_SIMULATION_MAX_TEXT_LENGTH, 20000)
export const MAX_SIMULATION_IMPORT_BYTES = configuredLimit(import.meta.env?.VITE_DEADLINE_SIMULATION_IMPORT_MAX_BYTES, 10 * 1024 * 1024)
export const MAX_SIMULATION_IMPORT_CASES = configuredLimit(import.meta.env?.VITE_DEADLINE_SIMULATION_IMPORT_MAX_CASES, 5000)

export const INITIAL_SIMULATION_FORM = {
  processNumber: '', court: 'TJSP', instance: '', state: '', city: '',
  courtDivision: '', communicationType: '', availabilityDate: '',
  publicationDate: '', captureDate: '', text: '',
}

const CONTEXT_FIELDS = ['publicationId', 'externalContext', 'representedParty', 'publicationRecipients', 'dataJudContext']
const DATE_FIELDS = ['availabilityDate', 'publicationDate', 'captureDate']
const COURT_LOCATION_FIELDS = ['state', 'city', 'comarca', 'judicialSection', 'subsection', 'courtUnit']

function normalizeContextObject(value, label, stringFields, { requiredName = false, verified = false, aliases = false } = {}) {
  if (value == null) return null
  if (typeof value !== 'object' || Array.isArray(value)) throw new Error(`Campo ${label} inválido: informe um objeto JSON.`)
  const fields = [...stringFields, ...(verified ? ['isVerified'] : []), ...(aliases ? ['aliases'] : [])]
  const normalized = {}
  const seen = new Set()
  for (const [key, fieldValue] of Object.entries(value)) {
    const field = fields.find((candidate) => candidate.toLowerCase() === key.toLowerCase())
    if (!field) throw new Error(`Campo não reconhecido em ${label}: ${key}.`)
    if (seen.has(field)) throw new Error(`Campo duplicado em ${label}: ${field}.`)
    seen.add(field)
    if (field === 'isVerified' && fieldValue == null) throw new Error(`Tipo inválido em ${label}.${field}.`)
    if (fieldValue != null) {
      if (field === 'isVerified' ? typeof fieldValue !== 'boolean'
        : field === 'aliases' ? !Array.isArray(fieldValue) || fieldValue.some((item) => typeof item !== 'string')
          : typeof fieldValue !== 'string') throw new Error(`Tipo inválido em ${label}.${field}.`)
      normalized[field] = clone(fieldValue)
    }
  }
  if (requiredName && !normalized.name?.trim()) throw new Error(`Informe o nome em ${label}.name.`)
  if (verified) normalized.isVerified = normalized.isVerified ?? false
  return normalized
}

function freezeSnapshot(value) {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freezeSnapshot)
    Object.freeze(value)
  }
  return value
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function normalizeInstance(value) {
  const normalized = String(value ?? '').trim().toUpperCase()
  if (!normalized) return ''
  if (['FIRST', '1ª INSTÂNCIA', '1A INSTANCIA', '1'].includes(normalized)) return 'FIRST'
  if (['SECOND', '2ª INSTÂNCIA', '2A INSTANCIA', '2'].includes(normalized)) return 'SECOND'
  if (['SUPERIOR', 'TRIBUNAL SUPERIOR'].includes(normalized)) return 'SUPERIOR'
  throw new Error('Instância não reconhecida no arquivo. Use FIRST, SECOND ou SUPERIOR.')
}

function validateDate(value, field) {
  if (value == null || value === '') return ''
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)
    || value.startsWith('0000-')
    || Number.isNaN(Date.parse(`${value}T00:00:00Z`))
    || new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) !== value) {
    throw new Error(`Data inválida em ${field}. Use YYYY-MM-DD sem conversão de fuso.`)
  }
  return value
}

export function parseSimulationImport(raw, fileName, fileSha256 = null) {
  let document
  try { document = JSON.parse(raw.replace(/^\uFEFF/, '')) } catch {
    throw new Error('O arquivo não contém JSON válido.')
  }
  const rows = Array.isArray(document) ? document
    : Array.isArray(document?.cases) ? document.cases : [document]
  if (!rows.length || rows.length > MAX_SIMULATION_IMPORT_CASES) throw new Error(`Importe entre 1 e ${MAX_SIMULATION_IMPORT_CASES.toLocaleString('pt-BR')} casos por arquivo.`)
  const ids = new Set()
  return rows.map((row, index) => {
    const source = row?.input?.payload ?? row?.payload ?? row?.request ?? row
    if (!source || typeof source !== 'object' || typeof source.text !== 'string' || !source.text.trim()) {
      throw new Error(`Caso ${index + 1}: o texto bruto está ausente. Use o cohort congelado ou um payload completo; referências de recuperação não contêm o texto.`)
    }
    if (source.text.length > MAX_SIMULATION_TEXT_LENGTH) {
      throw new Error(`Caso ${index + 1}: o texto excede ${MAX_SIMULATION_TEXT_LENGTH} caracteres. Nenhum texto foi truncado.`)
    }
    const form = { ...INITIAL_SIMULATION_FORM, court: '', instance: '', text: source.text }
    for (const field of ['processNumber', 'communicationType', 'courtDivision']) {
      if (source[field] != null && typeof source[field] !== 'string') throw new Error(`Campo inválido: ${field}.`)
      form[field] = source[field] ?? ''
    }
    form.court = source.court ?? source.tribunal ?? ''
    if (typeof form.court !== 'string') throw new Error('Tribunal inválido no arquivo.')
    form.instance = normalizeInstance(source.instance)
    for (const field of DATE_FIELDS) form[field] = validateDate(source[field], field)
    const courtLocation = normalizeContextObject(source.courtLocation, 'courtLocation', COURT_LOCATION_FIELDS)
    form.state = courtLocation?.state ?? source.state ?? ''
    form.city = courtLocation?.city ?? source.city ?? ''
    form.courtDivision = courtLocation?.courtUnit ?? form.courtDivision
    if (typeof form.state !== 'string' || typeof form.city !== 'string') throw new Error('Localidade inválida no arquivo.')
    const context = Object.fromEntries(CONTEXT_FIELDS.filter((field) => source[field] != null).map((field) => [field, clone(source[field])]))
    if (courtLocation) context.courtLocation = courtLocation
    if (context.publicationId != null && typeof context.publicationId !== 'string') throw new Error('Tipo inválido em publicationId.')
    if (context.externalContext != null) context.externalContext = normalizeContextObject(context.externalContext, 'externalContext',
      ['area', 'regime', 'microsystem', 'documentNature', 'source'], { verified: true })
    if (context.representedParty != null) context.representedParty = normalizeContextObject(context.representedParty, 'representedParty',
      ['name', 'role', 'source', 'referenceId'], { requiredName: true, verified: true, aliases: true })
    if (context.publicationRecipients != null) {
      if (!Array.isArray(context.publicationRecipients)) throw new Error('Campo publicationRecipients inválido: informe uma lista JSON.')
      context.publicationRecipients = context.publicationRecipients.map((recipient, recipientIndex) =>
        normalizeContextObject(recipient, `publicationRecipients[${recipientIndex}]`, ['name', 'role', 'source'], { requiredName: true }))
      if (context.publicationRecipients.some((recipient) => recipient == null)) throw new Error('Destinatário nulo em publicationRecipients.')
    }
    if (context.dataJudContext != null && (typeof context.dataJudContext !== 'object' || Array.isArray(context.dataJudContext))) {
      throw new Error('Campo dataJudContext inválido: informe um objeto JSON.')
    }
    const originalContext = Object.fromEntries([...CONTEXT_FIELDS, 'courtLocation'].filter((field) => source[field] != null)
      .map((field) => [field, clone(source[field])]))
    const caseId = String(row?.caseId ?? row?.input?.caseId ?? source.caseId ?? row?.ordinal ?? source.sourceId ?? `manual-${index + 1}`)
    if (ids.has(caseId)) throw new Error(`Identificador de caso duplicado: ${caseId}.`)
    ids.add(caseId)
    const expectedTextSha256 = source.textSha256 ?? row?.input?.sourceTextSha256 ?? null
    if (expectedTextSha256 != null && !/^[a-f\d]{64}$/i.test(expectedTextSha256)) throw new Error(`Hash de texto inválido no caso ${caseId}.`)
    return freezeSnapshot({ caseId, sourceId: source.sourceId ?? row?.input?.sourceId ?? null, ordinal: row?.ordinal ?? null,
      fileName, fileSha256, fileIndex: index, expectedTextSha256, form, context, originalContext })
  })
}

export function buildSimulationPayload(form, context = {}) {
  const payload = {}
  for (const field of ['processNumber', 'court', 'instance', 'communicationType', ...DATE_FIELDS]) {
    const value = String(form[field] ?? '').trim()
    if (value) payload[field] = DATE_FIELDS.includes(field) ? validateDate(value, field) : value
  }
  payload.text = form.text
  const courtLocation = Object.fromEntries(['comarca', 'judicialSection', 'subsection']
    .filter((field) => context.courtLocation?.[field] != null).map((field) => [field, context.courtLocation[field]]))
  if (form.state.trim()) courtLocation.state = form.state.trim().toUpperCase()
  if (form.city.trim()) courtLocation.city = form.city.trim()
  if (form.courtDivision.trim()) courtLocation.courtUnit = form.courtDivision.trim()
  if (Object.keys(courtLocation).length) payload.courtLocation = courtLocation
  for (const field of ['publicationId', 'representedParty', 'publicationRecipients']) {
    if (context[field] != null) payload[field] = clone(context[field])
  }
  if (payload.representedParty) payload.representedParty.isVerified = payload.representedParty.isVerified ?? false
  if (context.externalContext && !Array.isArray(context.externalContext) && typeof context.externalContext === 'object') {
    payload.externalContext = { ...clone(context.externalContext), source: 'USER_INPUT', isVerified: false }
  }
  return payload
}

export async function hashSimulationBytes(bytes) {
  if (!globalThis.crypto?.subtle) throw new Error('O navegador precisa de um contexto seguro para conferir a integridade do texto.')
  const hash = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function hashSimulationText(text) {
  return hashSimulationBytes(new TextEncoder().encode(text))
}

export async function createSimulationSnapshot(form, importedCase, requestId, startedAtUtc) {
  const formSnapshot = clone(form)
  const sourceTextSha256 = await hashSimulationText(formSnapshot.text)
  const matchesImportedText = importedCase?.form.text === formSnapshot.text
  if (matchesImportedText && importedCase.expectedTextSha256
    && sourceTextSha256 !== importedCase.expectedTextSha256.toLowerCase()) {
    throw new Error('O texto do caso diverge do SHA-256 registrado no arquivo. A simulação foi bloqueada.')
  }
  const payload = buildSimulationPayload(formSnapshot, importedCase?.context)
  payload.requestId = requestId
  payload.caseId = importedCase?.caseId ?? `manual-${requestId}`
  return freezeSnapshot({ requestId, caseId: payload.caseId, startedAtUtc, sourceTextSha256,
    sourceTextLength: formSnapshot.text.length, payload, import: importedCase ? {
      fileName: importedCase.fileName, fileSha256: importedCase.fileSha256, fileIndex: importedCase.fileIndex, sourceId: importedCase.sourceId,
      ordinal: importedCase.ordinal, expectedTextSha256: importedCase.expectedTextSha256,
      originalContext: clone(importedCase.originalContext), matchesImportedText,
      modified: JSON.stringify(formSnapshot) !== JSON.stringify(importedCase.form),
    } : null })
}

function comparable(value) {
  if (Array.isArray(value)) return value.map(comparable)
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort()
    .filter((key) => value[key] != null).map((key) => [key, comparable(value[key])]))
  return value
}

export async function verifySimulationReceipt(snapshot, result) {
  const receipt = result?.collectionEvidence
  if (!receipt) return { verified: false, errors: ['A API não devolveu a conferência da entrada. Atualize o backend antes de coletar novos casos.'] }
  const errors = []
  if (receipt.contractVersion !== 'simulation-collection-v1') errors.push('A versão do contrato de coleta não é compatível.')
  if (receipt.requestId !== snapshot.requestId) errors.push('O identificador da requisição recebida diverge.')
  if (receipt.caseId !== snapshot.caseId) errors.push('O identificador do caso recebido diverge.')
  if (JSON.stringify(comparable(receipt.receivedPayload)) !== JSON.stringify(comparable(snapshot.payload))) {
    errors.push('O texto ou os metadados recebidos divergem do payload enviado.')
  }
  if (receipt.sourceTextSha256 !== snapshot.sourceTextSha256
    || receipt.sourceTextLength !== snapshot.sourceTextLength) errors.push('A integridade do texto recebido diverge.')
  if (receipt.receivedPayload?.text != null && await hashSimulationText(receipt.receivedPayload.text) !== snapshot.sourceTextSha256) {
    errors.push('O hash informado não corresponde ao texto devolvido.')
  }
  if (!/^[a-f\d]{64}$/i.test(receipt.payloadHash ?? '')) errors.push('O hash do payload recebido está ausente ou inválido.')
  if (!receipt.runtimeIdentity || ['engineVersion', 'pipelineVersion', 'applicationAssemblyVersion', 'applicationModuleId', 'domainModuleId']
    .some((field) => !receipt.runtimeIdentity[field])) errors.push('A identificação da versão executada está ausente ou incompleta.')
  if (!result?.audit?.simulationId) errors.push('O identificador da simulação está ausente.')
  return freezeSnapshot({ verified: errors.length === 0, errors })
}

export function createSimulationEvidence(snapshot, response, integrity, completedAtUtc) {
  return freezeSnapshot({ schemaVersion: 'simulation-collection-v1', input: clone(snapshot),
    completedAtUtc, outcome: response.success ? (integrity.verified ? 'VERIFIED' : 'INTEGRITY_FAILURE') : 'REQUEST_FAILURE',
    integrity: clone(integrity), response: clone(response) })
}

export function simulationEvidenceFileName(evidence) {
  const safe = (value) => String(value).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80)
  return `deadline-case-${safe(evidence.input.caseId)}-simulation-${safe(evidence.response.data?.audit?.simulationId ?? 'failed')}-request-${safe(evidence.input.requestId)}.private.json`
}

export function downloadSimulationEvidence(evidence) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(evidence, null, 2)], { type: 'application/json;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = simulationEvidenceFileName(evidence)
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
